/**
 * Marks and labelled points used inside the Access-column help popover.
 *
 * Owners & access carries several controls whose effect is not guessable from
 * their label — "Act as owner" and "Send login link" both sound like they might
 * email somebody, and only one does. Explaining those next to the control that
 * needs them (rather than in a runbook nobody opens) is the difference between
 * a confident click and a cautious one.
 */
import { cn } from "@/lib/utils";

/**
 * The clause in a point that actually changes what you'd do.
 *
 * Explanations get skimmed, and the sentence that matters is rarely the first
 * one — "no email is sent" and "they never appear on an invoice run" are the
 * parts someone needs to catch at a glance. Marking them means the note can be
 * read in two seconds or in full, and both readings land the same facts.
 *
 * `warn` is for a consequence that could surprise; `good` is for a reassurance
 * that stops someone hesitating over a safe action.
 */
export function HelpMark({
  tone = "warn",
  children,
}: {
  tone?: "warn" | "good";
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "rounded px-1 py-px font-medium",
        tone === "warn"
          ? "bg-amber-100/70 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200"
          : "bg-emerald-100/70 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200"
      )}
    >
      {children}
    </span>
  );
}

/** One labelled point inside a help popover — a term, then what it means. */
export function HelpPoint({
  term,
  children,
}: {
  term: string;
  children: React.ReactNode;
}) {
  return (
    <p>
      <span className="font-medium text-foreground">{term}</span>
      {" — "}
      {children}
    </p>
  );
}
